/**
 * PublicSharedHeader — a compact marketing-friendly header for the /l/:slug/r/:token public view.
 *
 * Replaces the full letterhead ONLY on public shared reports. Renders:
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │  [Lab Logo]        [ Promotional Banner (optional) ]     📞 +91 ...  │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * All fields degrade gracefully when unset (logo-only, no banner, etc.).
 */
import { Phone } from "lucide-react";
import LabPromoBannerCarousel from "@/components/lab/LabPromoBannerCarousel";

export default function PublicSharedHeader({ lab }) {
  if (!lab) return null;
  const logo = lab.logo_url;
  // Multi-banner carousel (new). Falls back to legacy single banner_url so labs
  // that haven't migrated still see their existing banner rendered.
  const banners = (lab.promo_banners || []).filter((b) => b?.image_url);
  const legacyBanner = !banners.length && lab.booking_promo_banner_url
    ? [{ id: "legacy", image_url: lab.booking_promo_banner_url, link_url: lab.booking_promo_banner_link, title: null }]
    : null;
  const finalBanners = banners.length ? banners : (legacyBanner || []);
  // Prefer the dedicated booking number, fallback chain: booking → customer_care → phone → whatsapp.
  const primaryPhone =
    lab.booking_whatsapp_number || lab.customer_care || lab.phone || lab.whatsapp_number;
  const primaryLabel = lab.booking_whatsapp_number
    ? "Book on WhatsApp"
    : lab.customer_care
      ? "Customer care"
      : "Call us";

  return (
    <header
      data-testid="public-shared-header"
      className="w-full bg-white border-b border-border/60"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 space-y-3 sm:space-y-0 sm:grid sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-4">
        {/* Top row on mobile: logo (left) + phone (right). On desktop: same row also holds the banner in the middle. */}
        <div className="flex items-center justify-between gap-3 sm:contents">
          {/* Logo */}
          <div className="shrink-0" data-testid="psh-logo-slot">
            {logo ? (
              <img
                src={logo}
                alt={lab.name || "Lab logo"}
                className="h-10 sm:h-12 w-auto object-contain"
                data-testid="psh-logo"
              />
            ) : (
              <div className="font-heading font-bold text-lg text-ink" data-testid="psh-name">
                {lab.name || "EFGH"}
              </div>
            )}
          </div>

          {/* Booking / customer care number */}
          {primaryPhone ? (
            <a
              href={`tel:${String(primaryPhone).replace(/[^\d+]/g, "")}`}
              className="shrink-0 inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-ink text-white text-xs sm:text-sm font-medium hover:bg-ink/90 sm:order-3"
              data-testid="psh-cta"
            >
              <Phone className="w-4 h-4" />
              <div className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-[0.14em] opacity-80">{primaryLabel}</span>
                <span className="font-heading font-semibold text-sm">{primaryPhone}</span>
              </div>
              <span className="sm:hidden font-heading font-semibold text-sm">{primaryPhone}</span>
            </a>
          ) : null}
        </div>

        {/* Promotional banner — full width on mobile (its own row), centered inline on ≥sm */}
        <div className="min-w-0 sm:order-2" data-testid="psh-banner-slot">
          {finalBanners.length > 0 ? (
            <LabPromoBannerCarousel banners={finalBanners} labId={lab.id} height="h-28 sm:h-24 md:h-28 lg:h-32" />
          ) : (
            <div className="hidden sm:block text-center text-[11px] uppercase tracking-[0.22em] text-ink/40" data-testid="psh-tagline">
              {lab.tagline || "Smart diagnostic reporting"}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
